import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Clock, User, GitCommit, RotateCcw } from "lucide-react";
import { adminHistory, adminVersions, adminGetListing, adminRevertLatest } from "../../lib/adminApi";

const ACTION_LABEL = {
  created: "Created",
  edited: "Edited",
  published: "Published",
  unpublished: "Unpublished",
  archived: "Archived",
  restored_from_archive: "Restored from Archive",
  trashed: "Moved to Trash",
  restored_from_trash: "Restored from Trash",
  permanently_deleted: "Permanently deleted",
  duplicated: "Duplicated",
  featured_changed: "Featured toggled",
  sponsored_changed: "Sponsored toggled",
  reverted: "Reverted",
  orphan_cleaned: "Orphan cleanup",
};

export default function HistoryView() {
  const { id } = useParams();
  const [entries, setEntries] = useState([]);
  const [versions, setVersions] = useState([]);
  const [listing, setListing] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [h, v, l] = await Promise.all([
        adminHistory(id), adminVersions(id), adminGetListing(id).catch(()=>null)
      ]);
      setEntries(h); setVersions(v); setListing(l);
    } catch { toast.error("Could not load history"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [id]);

  const revert = async () => {
    if (!window.confirm("Revert to the previous saved snapshot? Any unsaved edits will be lost.")) return;
    try { await adminRevertLatest(id); toast.success("Reverted"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Revert failed"); }
  };

  return (
    <div className="max-w-[1200px] mx-auto px-6 py-10" data-testid="publisher-history">
      <div className="flex items-center gap-4 mb-6">
        <Link to="/admin/listings" className="inline-flex items-center gap-2 font-mono-wr text-[10px] uppercase tracking-widest hover:text-[var(--wr-red)]" data-testid="history-back">
          <ArrowLeft size={14}/> Listings
        </Link>
        <div>
          <p className="font-mono-wr text-[11px] uppercase tracking-[0.3em] text-[var(--wr-red)]">/ PUBLISHER · HISTORY</p>
          <h1 className="font-editorial text-4xl mt-1">{listing?.name_en || listing?.name_ro || "Listing history"}</h1>
        </div>
        <div className="ml-auto flex gap-2">
          {listing && <Link to={`/admin/edit/${id}`} className="px-4 py-2 border border-[var(--wr-border)] hover:border-[var(--wr-ink)] font-mono-wr text-[10px] uppercase tracking-widest" data-testid="history-edit">Back to edit</Link>}
          {versions.length > 0 && !listing?.trashed_at && (
            <button onClick={revert} className="inline-flex items-center gap-1.5 px-4 py-2 border border-[var(--wr-red)] text-[var(--wr-red)] hover:bg-[var(--wr-red)] hover:text-white font-mono-wr text-[10px] uppercase tracking-widest" data-testid="history-revert">
              <RotateCcw size={12}/> Revert latest change
            </button>
          )}
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Audit log */}
        <div className="lg:col-span-2 border border-[var(--wr-border)]" data-testid="history-audit">
          <p className="font-mono-wr text-[10px] uppercase tracking-widest text-[var(--wr-ink-2)] p-4 border-b border-[var(--wr-border)]">Audit log</p>
          {loading ? <p className="p-6 text-[var(--wr-ink-2)]">Loading…</p>
          : entries.length === 0 ? <p className="p-6 text-[var(--wr-ink-2)]">No events yet.</p>
          : <ol className="divide-y divide-[var(--wr-border)]">
              {entries.map(e => (
                <li key={e.id} className="p-4" data-testid={`audit-entry-${e.id}`}>
                  <div className="flex items-baseline gap-3 flex-wrap">
                    <span className="font-editorial text-lg">{ACTION_LABEL[e.action] || e.action}</span>
                    <span className="font-mono-wr text-[10px] uppercase tracking-widest text-[var(--wr-ink-2)]"><Clock size={10} className="inline mr-1"/>{new Date(e.timestamp).toLocaleString()}</span>
                    <span className="font-mono-wr text-[10px] uppercase tracking-widest text-[var(--wr-ink-2)]"><User size={10} className="inline mr-1"/>{e.actor}</span>
                  </div>
                  {e.note && <p className="mt-1 text-[13px] text-[var(--wr-ink-2)]">{e.note}</p>}
                  {e.changed_fields?.length > 0 && (
                    <details className="mt-2">
                      <summary className="cursor-pointer font-mono-wr text-[10px] uppercase tracking-widest text-[var(--wr-red)]">
                        {e.changed_fields.length} changed field{e.changed_fields.length===1?"":"s"} — see diff
                      </summary>
                      <div className="mt-2 space-y-2">
                        {Object.entries(e.diff || {}).map(([field, ba]) => (
                          <div key={field} className="border border-[var(--wr-border)] bg-[var(--wr-surface)] p-3">
                            <p className="font-mono-wr text-[10px] uppercase tracking-widest text-[var(--wr-ink-2)] mb-1">{field}</p>
                            <div className="grid sm:grid-cols-2 gap-3 text-[12px]">
                              <div><span className="text-[var(--wr-red)] font-mono-wr text-[9px] uppercase">− before</span><pre className="mt-1 whitespace-pre-wrap break-all">{JSON.stringify(ba.before, null, 0)}</pre></div>
                              <div><span className="text-emerald-700 font-mono-wr text-[9px] uppercase">+ after</span><pre className="mt-1 whitespace-pre-wrap break-all">{JSON.stringify(ba.after, null, 0)}</pre></div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </details>
                  )}
                </li>
              ))}
            </ol>}
        </div>

        {/* Version snapshots */}
        <div className="border border-[var(--wr-border)]" data-testid="history-versions">
          <p className="font-mono-wr text-[10px] uppercase tracking-widest text-[var(--wr-ink-2)] p-4 border-b border-[var(--wr-border)]">Version snapshots</p>
          {versions.length === 0 ? <p className="p-6 text-[var(--wr-ink-2)] text-[13px]">Snapshots are stored on state changes (publish, archive, trash, feature, duplicate). None yet.</p>
          : <ol className="divide-y divide-[var(--wr-border)]">
              {versions.map(v => (
                <li key={v.id} className="p-4" data-testid={`version-${v.id}`}>
                  <p className="font-mono-wr text-[10px] uppercase tracking-widest text-[var(--wr-red)] flex items-center gap-1"><GitCommit size={11}/> {v.reason}</p>
                  <p className="mt-1 text-[12px] text-[var(--wr-ink-2)]">{new Date(v.created_at).toLocaleString()} · {v.actor}</p>
                  <p className="mt-1 font-mono-wr text-[10px] text-[var(--wr-ink-2)]">
                    status <span className="text-[var(--wr-ink)]">{v.snapshot?.status || "—"}</span>
                    {v.snapshot?.featured ? " · featured" : ""}
                  </p>
                </li>
              ))}
            </ol>}
        </div>
      </div>
    </div>
  );
}
